import * as THREE from 'three';

/**
 * 共享交互材质的 `userData` 标记字段。
 *
 * 设计说明：
 * 1. 交互状态机为了降低 hover / selected 高频切换成本，会缓存并复用部分材质；
 * 2. 几何代理释放时只能销毁自己独占的材质，不能销毁仍被状态机缓存持有的共享材质；
 * 3. 用材质自身的 `userData` 记录所有权，避免把状态机实现细节泄漏到 `FeatureResultMesh`。
 */
const SHARED_INTERACTION_MATERIAL_FLAG = 'isSharedInteractionMaterial';

/**
 * 标记材质由交互状态机共享持有。
 *
 * @param material 需要标记的材质。
 * @returns 原材质实例，便于函数式链式使用。
 */
export function markSharedInteractionMaterial<TMaterial extends THREE.Material>(
  material: TMaterial,
): TMaterial {
  material.userData = {
    ...material.userData,
    [SHARED_INTERACTION_MATERIAL_FLAG]: true,
  };
  return material;
}

/**
 * 判断材质是否由交互状态机共享持有。
 *
 * @param material 待判断材质。
 * @returns 是否为共享交互材质。
 */
export function isSharedInteractionMaterial(material: THREE.Material): boolean {
  return material.userData?.[SHARED_INTERACTION_MATERIAL_FLAG] === true;
}

/**
 * 从共享渲染配方创建独占材质，供短生命周期覆盖对象建立可释放的 RenderObject 边界。
 *
 * Material.clone 会复制 userData，共享标记必须在所有权领域统一移除；其它配方数据保留。
 * 纹理仍遵循 Three.js 的借用规则，调用方只拥有返回材质，不拥有其引用的纹理。
 * @param source 原渲染配方，不会被修改或释放。
 * @returns 必须由调用方 dispose 的同类型材质副本。
 */
export function cloneOwnedMaterial<TMaterial extends THREE.Material>(source: TMaterial): TMaterial {
  const owned = source.clone();
  delete owned.userData[SHARED_INTERACTION_MATERIAL_FLAG];
  return owned;
}

/**
 * 只释放当前对象独占持有的材质。
 *
 * @param material 材质或材质数组。
 */
export function disposeOwnedMaterial(
  material: THREE.Material | THREE.Material[] | null | undefined,
): void {
  if (!material) {
    return;
  }

  if (Array.isArray(material)) {
    material.forEach((item) => {
      if (!isSharedInteractionMaterial(item)) {
        item.dispose();
      }
    });
    return;
  }

  if (!isSharedInteractionMaterial(material)) {
    material.dispose();
  }
}
